import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, Renderer2, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'password-input',
    templateUrl: './password-input.component.html',
    styleUrls: ['./password-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PasswordInputComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class PasswordInputComponent implements ControlValueAccessor, AfterViewInit {
    @Input() placeholder = 'Password';
    @Input() label = 'Password';
    @Input() showLabel = true;
    @Input() id = 'password';
    @Input() name = 'password';
    @Input() autocomplete = 'off';

    value = '';
    private disabled = false;
    inputType: INPUT_TYPE = INPUT_TYPE.PASSWORD;

    @ViewChild('passwordInput') passwordInput: ElementRef;
    @ViewChild('visibleButton') visibleButton: ElementRef;

    constructor(private renderer: Renderer2) {}

    onChange: any = () => {};
    onTouch: any = () => {};

    set password(password) {
        this.value = password;
        this.onChange(password);
        this.onTouch(password);
    }

    get password() {
        return this.value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    writeValue(obj: any): void {
        this.password = obj;
    }

    /**
     * Angular 15 calls setDisabledState while the form control is being set up, which is before the
     * view exists -- reading passwordInput.nativeElement there threw on every page carrying this
     * control. Remember the state instead and apply it once the view is there.
     */
    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
        this.applyDisabledState();
    }

    ngAfterViewInit(): void {
        this.applyDisabledState();
    }

    private applyDisabledState(): void {
        if (!this.passwordInput || !this.visibleButton) {
            return;
        }
        this.renderer.setProperty(this.passwordInput.nativeElement, 'disabled', this.disabled);
        this.renderer.setProperty(this.visibleButton.nativeElement, 'disabled', this.disabled);
    }

    changeVisibility() {
        if (this.inputType === INPUT_TYPE.PASSWORD) this.inputType = INPUT_TYPE.TEXT;
        else if (this.inputType === INPUT_TYPE.TEXT) this.inputType = INPUT_TYPE.PASSWORD;
    }
}

class INPUT_TYPE {
    static readonly TEXT = new INPUT_TYPE('text');
    static readonly PASSWORD = new INPUT_TYPE('password');

    constructor(public readonly html: string) {}
}
